// src/pages/disputas/DisputaDetallePage.tsx
//
// Página de detalle completo de una disputa — diseño responsive.
// Ruta: /disputas/:id
// Usa .disputa-detalle-grid del CSS global (1fr 340px → 1fr en ≤991px)

import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { getDisputaDetalle } from "../../api/disputaApi";
import type { Disputa } from "../../api/disputaApi";

// ── helpers ───────────────────────────────────────────────────────────────────

const formatDate = (d?: string | null) => {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("es-CO", {
    day: "2-digit",
    month: "long",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const ESTADO_META: Record<
  string,
  { color: string; bg: string; border: string; icon: string; label: string }
> = {
  ABIERTA: {
    color: "#b45309",
    bg: "#fffbeb",
    border: "#fde68a",
    icon: "bi-hourglass-split",
    label: "Abierta",
  },
  EN_REVISION: {
    color: "#1d4ed8",
    bg: "#eff6ff",
    border: "#bfdbfe",
    icon: "bi-eye-fill",
    label: "En revisión",
  },
  RESUELTA: {
    color: "#15803d",
    bg: "#f0fdf4",
    border: "#bbf7d0",
    icon: "bi-check-circle-fill",
    label: "Resuelta",
  },
  CERRADA: {
    color: "#6b7280",
    bg: "#f9fafb",
    border: "#e5e7eb",
    icon: "bi-x-circle-fill",
    label: "Cerrada",
  },
};

// ── sub-componentes ───────────────────────────────────────────────────────────

function InfoRow({ label, value }: { label: string; value?: string | null }) {
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "flex-start",
        gap: 12,
        padding: "7px 0",
        borderBottom: "1px solid rgba(0,0,0,0.05)",
        flexWrap: "wrap",
      }}
    >
      <span
        style={{
          fontSize: "0.8rem",
          fontWeight: 600,
          color: "#6b7280",
          flexShrink: 0,
          minWidth: 120,
        }}
      >
        {label}
      </span>
      <span
        style={{
          fontSize: "0.875rem",
          color: "#111827",
          wordBreak: "break-word",
          flex: 1,
          textAlign: "right",
        }}
      >
        {value || "—"}
      </span>
    </div>
  );
}

function SectionCard({
  color,
  bg,
  border,
  icon,
  title,
  children,
}: {
  color: string;
  bg: string;
  border: string;
  icon: string;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div
      style={{
        background: bg,
        border: `1.5px solid ${border}`,
        borderRadius: 14,
        padding: "18px 20px",
        marginBottom: 16,
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          marginBottom: 14,
        }}
      >
        <div
          style={{
            width: 32,
            height: 32,
            borderRadius: 9,
            flexShrink: 0,
            background: color + "18",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <i className={`bi ${icon}`} style={{ color, fontSize: "0.95rem" }} />
        </div>
        <span style={{ fontWeight: 700, fontSize: "0.93rem", color }}>
          {title}
        </span>
      </div>
      {children}
    </div>
  );
}

// ── página ────────────────────────────────────────────────────────────────────

export default function DisputaDetallePage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [disputa, setDisputa] = useState<Disputa | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const d = await getDisputaDetalle(id);
      if (d) setDisputa(d);
      else setError("No se encontró la disputa.");
    } catch {
      setError("Error al cargar la disputa. Intenta de nuevo.");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  // ── loading ──────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight: 300,
        }}
      >
        <div
          className="spinner-border"
          style={{ color: "var(--fg-green)", width: 40, height: 40 }}
        />
        <p style={{ marginTop: 16, color: "var(--fg-muted)" }}>
          Cargando disputa...
        </p>
      </div>
    );
  }

  // ── error ────────────────────────────────────────────────────────────────
  if (error || !disputa) {
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight: 300,
          textAlign: "center",
          padding: 24,
        }}
      >
        <i
          className="bi bi-exclamation-triangle"
          style={{ fontSize: 40, color: "var(--fg-orange)" }}
        />
        <p
          style={{
            marginTop: 12,
            color: "var(--fg-muted)",
            fontSize: "0.95rem",
          }}
        >
          {error || "Disputa no encontrada."}
        </p>
        <button
          className="btn-fg-outline"
          style={{ marginTop: 16 }}
          onClick={() => navigate("/disputas")}
        >
          <i className="bi bi-arrow-left me-2" /> Volver a disputas
        </button>
      </div>
    );
  }

  const meta = ESTADO_META[disputa.estado] ?? ESTADO_META.CERRADA;

  return (
    <div style={{ maxWidth: 1100, margin: "0 auto", paddingBottom: 40 }}>
      {/* ── Breadcrumb + back ─────────────────────────────────────────── */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          marginBottom: 20,
          flexWrap: "wrap",
        }}
      >
        <button
          className="btn-fg-outline"
          style={{ padding: "7px 14px", fontSize: "0.875rem" }}
          onClick={() => navigate("/disputas")}
        >
          <i className="bi bi-arrow-left me-1" /> Volver
        </button>
        <nav
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            fontSize: "0.875rem",
            flexWrap: "wrap",
          }}
        >
          <span
            style={{ color: "var(--fg-muted)", cursor: "pointer" }}
            onClick={() => navigate("/disputas")}
          >
            Disputas
          </span>
          <i
            className="bi bi-chevron-right"
            style={{ fontSize: "0.7rem", color: "var(--fg-muted)" }}
          />
          <span style={{ color: "var(--fg-text)", fontWeight: 600 }}>
            Detalle #{disputa.id.slice(0, 8).toUpperCase()}
          </span>
        </nav>
      </div>

      {/* ── Header card ──────────────────────────────────────────────── */}
      <div
        style={{
          background: "white",
          borderRadius: 16,
          padding: "22px 24px",
          marginBottom: 20,
          boxShadow: "var(--shadow)",
          border: "1px solid rgba(0,0,0,0.05)",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          gap: 16,
          flexWrap: "wrap",
        }}
      >
        <div style={{ flex: 1, minWidth: 220 }}>
          {/* Estado badge */}
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 7,
              borderRadius: 20,
              padding: "5px 14px",
              fontWeight: 700,
              fontSize: "0.8rem",
              background: meta.bg,
              border: `1.5px solid ${meta.border}`,
              color: meta.color,
              marginBottom: 12,
            }}
          >
            <i className={`bi ${meta.icon}`} style={{ fontSize: "0.85rem" }} />
            {meta.label}
          </div>
          <h1
            style={{
              margin: 0,
              fontSize: "clamp(1.1rem, 3vw, 1.35rem)",
              fontWeight: 700,
              color: "var(--fg-text)",
              lineHeight: 1.3,
              marginBottom: 8,
              wordBreak: "break-word",
            }}
          >
            {disputa.asunto}
          </h1>
          <p
            style={{ margin: 0, fontSize: "0.85rem", color: "var(--fg-muted)" }}
          >
            <i className="bi bi-clock me-1" />
            Abierta el {formatDate(disputa.fechaApertura)}
          </p>
        </div>
        {/* ID Disputa */}
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-end",
            gap: 4,
            flexShrink: 0,
          }}
        >
          <span
            style={{
              fontSize: "0.7rem",
              fontWeight: 600,
              color: "var(--fg-muted)",
              textTransform: "uppercase",
              letterSpacing: "0.05em",
            }}
          >
            ID Disputa
          </span>
          <code
            style={{
              fontSize: "0.78rem",
              background: "#f3f4f6",
              padding: "4px 10px",
              borderRadius: 8,
              color: "#374151",
              wordBreak: "break-all",
            }}
          >
            {disputa.id.slice(0, 16)}…
          </code>
        </div>
      </div>

      {/* ── Grid principal — 2 columnas desktop, 1 en tablet/móvil ─── */}
      <div className="disputa-detalle-grid">
        {/* Columna izquierda */}
        <div>
          {/* Servicio */}
          <SectionCard
            color="#006b4c"
            bg="#f0fdf4"
            border="#bbf7d0"
            icon="bi-clipboard2-pulse-fill"
            title="Servicio disputado"
          >
            <InfoRow
              label="Nombre del servicio"
              value={disputa.servicioNombre}
            />
            <InfoRow label="ID de solicitud" value={disputa.solicitudId} />
            <InfoRow label="Método de pago" value={disputa.metodoPago} />
          </SectionCard>

          {/* Inconformidad */}
          <SectionCard
            color="#b45309"
            bg="#fffbeb"
            border="#fde68a"
            icon="bi-exclamation-triangle-fill"
            title="Inconformidad presentada"
          >
            <InfoRow
              label="Presentada por"
              value={`${disputa.autorNombre} · ${disputa.rolAutor === "CLIENTE" ? "👤 Cliente" : "🩺 MVZ"}`}
            />
            <InfoRow
              label="Fecha apertura"
              value={formatDate(disputa.fechaApertura)}
            />
            <InfoRow label="Asunto" value={disputa.asunto} />
            <div style={{ marginTop: 12 }}>
              <span
                style={{
                  fontSize: "0.8rem",
                  fontWeight: 600,
                  color: "#6b7280",
                }}
              >
                Descripción completa
              </span>
              <div
                style={{
                  marginTop: 8,
                  padding: "12px 14px",
                  background: "rgba(0,0,0,0.03)",
                  borderRadius: 10,
                  fontSize: "0.88rem",
                  color: "#374151",
                  lineHeight: 1.6,
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                }}
              >
                {disputa.descripcion}
              </div>
            </div>
          </SectionCard>

          {/* Respuesta contraparte */}
          {disputa.respuesta ? (
            <SectionCard
              color="#1d4ed8"
              bg="#eff6ff"
              border="#bfdbfe"
              icon="bi-chat-text-fill"
              title={`Respuesta de ${disputa.notificadoNombre ?? "la contraparte"}`}
            >
              <InfoRow
                label="Respondida por"
                value={disputa.notificadoNombre}
              />
              <InfoRow
                label="Fecha respuesta"
                value={formatDate(disputa.fechaRespuesta)}
              />
              <div style={{ marginTop: 12 }}>
                <span
                  style={{
                    fontSize: "0.8rem",
                    fontWeight: 600,
                    color: "#6b7280",
                  }}
                >
                  Respuesta
                </span>
                <div
                  style={{
                    marginTop: 8,
                    padding: "12px 14px",
                    background: "rgba(0,0,0,0.03)",
                    borderRadius: 10,
                    fontSize: "0.88rem",
                    color: "#374151",
                    lineHeight: 1.6,
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                  }}
                >
                  {disputa.respuesta}
                </div>
              </div>
            </SectionCard>
          ) : (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                padding: "28px 20px",
                background: "#f9fafb",
                border: "1.5px dashed #e5e7eb",
                borderRadius: 14,
                marginBottom: 16,
                textAlign: "center",
              }}
            >
              <i
                className="bi bi-chat-dots"
                style={{ fontSize: 28, color: "#9ca3af", marginBottom: 8 }}
              />
              <p style={{ margin: 0, color: "#9ca3af", fontSize: "0.875rem" }}>
                La contraparte aún no ha respondido a esta inconformidad.
              </p>
            </div>
          )}
        </div>

        {/* Columna derecha */}
        <div>
          {/* Partes involucradas */}
          <div className="fg-card" style={{ marginBottom: 16 }}>
            <div className="fg-card-header">
              <i
                className="bi bi-people-fill"
                style={{ color: "var(--fg-green)", fontSize: "1rem" }}
              />
              <span className="fg-card-title">Partes involucradas</span>
            </div>
            <div style={{ padding: "14px 18px" }}>
              {/* Autor */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                  padding: "10px 0",
                }}
              >
                <div
                  style={{
                    width: 42,
                    height: 42,
                    borderRadius: 12,
                    flexShrink: 0,
                    background: "#fef3c7",
                    color: "#b45309",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "1.2rem",
                  }}
                >
                  {disputa.rolAutor === "CLIENTE" ? "👤" : "🩺"}
                </div>
                <div style={{ minWidth: 0 }}>
                  <div
                    style={{
                      fontWeight: 600,
                      fontSize: "0.88rem",
                      color: "#111827",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {disputa.autorNombre}
                  </div>
                  <div
                    style={{
                      fontSize: "0.75rem",
                      color: "#6b7280",
                      marginTop: 2,
                    }}
                  >
                    {disputa.rolAutor === "CLIENTE"
                      ? "Cliente — Presenta la disputa"
                      : "MVZ — Presenta la disputa"}
                  </div>
                </div>
              </div>
              <div
                style={{
                  height: 1,
                  background: "rgba(0,0,0,0.06)",
                  margin: "4px 0",
                }}
              />
              {/* Notificado */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                  padding: "10px 0",
                }}
              >
                <div
                  style={{
                    width: 42,
                    height: 42,
                    borderRadius: 12,
                    flexShrink: 0,
                    background: "#dbeafe",
                    color: "#1d4ed8",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "1.2rem",
                  }}
                >
                  {disputa.rolAutor === "CLIENTE" ? "🩺" : "👤"}
                </div>
                <div style={{ minWidth: 0 }}>
                  <div
                    style={{
                      fontWeight: 600,
                      fontSize: "0.88rem",
                      color: "#111827",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {disputa.notificadoNombre ?? "Sin asignar"}
                  </div>
                  <div
                    style={{
                      fontSize: "0.75rem",
                      color: "#6b7280",
                      marginTop: 2,
                    }}
                  >
                    {disputa.rolAutor === "CLIENTE"
                      ? "MVZ — Parte notificada"
                      : "Cliente — Parte notificada"}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Resolución admin */}
          {disputa.resolucionAdmin || disputa.favorDe ? (
            <div
              className="fg-card"
              style={{ border: "1.5px solid #bbf7d0", marginBottom: 16 }}
            >
              <div className="fg-card-header" style={{ background: "#f0fdf4" }}>
                <i
                  className="bi bi-patch-check-fill"
                  style={{ color: "#15803d", fontSize: "1rem" }}
                />
                <span className="fg-card-title" style={{ color: "#15803d" }}>
                  Resolución del administrador
                </span>
              </div>
              <div style={{ padding: "14px 18px" }}>
                {disputa.favorDe && (() => {
                  const bg     = disputa.favorDe === 'CLIENTE' ? '#fef3c7'
                               : disputa.favorDe === 'MVZ'     ? '#dcfce7'
                               : '#eff6ff';
                  const color  = disputa.favorDe === 'CLIENTE' ? '#92400e'
                               : disputa.favorDe === 'MVZ'     ? '#166534'
                               : '#1e40af';
                  const icon   = disputa.favorDe === 'CLIENTE' ? '👤'
                               : disputa.favorDe === 'MVZ'     ? '🩺' : '🤝';
                  const impacto = disputa.favorDe === 'CLIENTE'
                    ? '⚠️ El MVZ NO puede retirar el valor de este servicio'
                    : disputa.favorDe === 'MVZ'
                    ? '✅ El MVZ puede retirar el valor de este servicio'
                    : 'ℹ️ Sin bloqueo financiero — mediación';
                  return (
                    <div style={{ background: bg, borderRadius: 10, padding: '10px 14px', marginBottom: 12 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
                        <span style={{ fontSize: '0.8rem', fontWeight: 600, color }}>Fallo a favor de</span>
                        <span style={{ fontSize: '0.9rem', fontWeight: 700, color }}>
                          {icon} {disputa.favorDe}
                        </span>
                      </div>
                      <div style={{ fontSize: '0.75rem', color, marginTop: 6, fontStyle: 'italic' }}>
                        💰 {impacto}
                      </div>
                    </div>
                  );
                })()}
                <InfoRow
                  label="Fecha resolución"
                  value={formatDate(disputa.fechaResolucion)}
                />
                {disputa.resolucionAdmin && (
                  <div style={{ marginTop: 12 }}>
                    <span
                      style={{
                        fontSize: "0.8rem",
                        fontWeight: 600,
                        color: "#6b7280",
                      }}
                    >
                      Decisión
                    </span>
                    <div
                      style={{
                        marginTop: 8,
                        padding: "12px 14px",
                        background: "rgba(0,0,0,0.03)",
                        borderRadius: 10,
                        fontSize: "0.88rem",
                        color: "#374151",
                        lineHeight: 1.6,
                        whiteSpace: "pre-wrap",
                        wordBreak: "break-word",
                      }}
                    >
                      {disputa.resolucionAdmin}
                    </div>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="fg-card" style={{ marginBottom: 16 }}>
              <div className="fg-card-header">
                <i
                  className="bi bi-hourglass-split"
                  style={{ color: "var(--fg-orange)", fontSize: "1rem" }}
                />
                <span className="fg-card-title">Pendiente de resolución</span>
              </div>
              <div
                style={{
                  padding: "24px 20px",
                  textAlign: "center",
                  color: "var(--fg-muted)",
                  fontSize: "0.875rem",
                }}
              >
                <i
                  className="bi bi-clock-history"
                  style={{
                    fontSize: 32,
                    display: "block",
                    marginBottom: 10,
                    color: "#d1d5db",
                  }}
                />
                Esta disputa aún no tiene resolución por parte del
                administrador.
              </div>
            </div>
          )}

          <button
            className="btn-fg-outline"
            style={{ width: "100%", justifyContent: "center" }}
            onClick={() => navigate("/disputas")}
          >
            <i className="bi bi-arrow-left me-2" /> Volver a la lista
          </button>
        </div>
      </div>
    </div>
  );
}
